import { useEffect, useState } from 'react'
import { isEmpty, isNil } from 'lodash'
import { connect, useDispatch } from 'react-redux'
import { Link, useNavigate, useParams } from 'react-router-dom'

import routes, { categoriesGroup } from '../../routers'
import { setLoading } from '../../redux/actions/ui'
import { deleteRecipe, getRecipeById, recordRecipeAction } from '../../api/recipe'
import { trackAction } from '../../utils/tracking'
import { setReduceNumberRecipes } from '../../redux/actions/user'
import Layout from '../../components/Layout'
import ButtonReport from '../../components/ButtonReport'
import TableUsers from '../../components/TableUsers'
import UserPhoto from '../../components/UserPhoto'
import RecipeAudioPlayer from '../../components/RecipeAudioPlayer'
import { setVoiceLink } from '../../api/cookbook'
import { AddToCookbook } from '../../components/CookbookPromo'
import RecipeComments from '../../components/RecipeComments'
import { addedByLine, creditName, creditPerson } from '../../utils/recipeCredit'
import RecipeHero from './components/RecipeHero'
import RecipePhotoViewer from './components/RecipePhotoViewer'
import IngredientChecklist from './components/IngredientChecklist'

import './style.css'

// The API sends this placeholder when a recipe has no food photo
const NO_PHOTO_PATTERN = /imageRecipeWithoutPhoto\.png$/

// Only ordinary web links become a button
const safeWebLink = (link) => (/^https?:\/\/\S+$/i.test(link || '') ? link : null)

const websiteName = (link) => {
  try {
    return new URL(link).hostname.replace(/^www\./, '')
  } catch (e) {
    return ''
  }
}

const Dashboard = ({ user }) => {
  const dispatch = useDispatch()
  const navigation = useNavigate()
  const { recipeId, idGroup, idSlug } = useParams()
  const [dataRecipe, setDataRecipe] = useState({})
  const [shownPhoto, setShownPhoto] = useState(null)
  const [locked, setLocked] = useState(null)
  // Photos the family posted in the recipe's activity, newest first. They get their own
  // full-screen viewer: the recipe's own photos (above) are the ones cookbooks print.
  const [familyPhotos, setFamilyPhotos] = useState([])
  const [shownFamilyPhoto, setShownFamilyPhoto] = useState(null)

  const servingSize = `${!isNil(dataRecipe?.amount) ? dataRecipe?.amount : ''} ${
    !isNil(dataRecipe?.measurement) ? dataRecipe?.measurement : ''
  }`
  const checkCurrentUser = user?.id === dataRecipe?.user_id
  // A family member who added this recipe for its owner can still edit it
  const canEdit = checkCurrentUser || (!!user?.id && user.id === dataRecipe?.added_by_user_id)

  // The food photo first, then the photos of the original card
  const hasFoodPhoto = !!dataRecipe?.image && !NO_PHOTO_PATTERN.test(dataRecipe.image)
  const photos = [
    ...(hasFoodPhoto ? [{ image: dataRecipe.image, kind: 'food', label: 'Photo' }] : []),
    ...(dataRecipe?.cardImages || []).map(({ image }) => ({
      image,
      kind: 'card',
      label: 'Original recipe card',
    })),
  ]

  const deleteMyRecipe = () => {
    deleteRecipe(recipeId)
      .then(() => {
        dispatch(setReduceNumberRecipes())
        !isNil(idGroup)
          ? navigation(categoriesGroup(idSlug, idGroup))
          : navigation(routes.userProfile)
      })
      .catch((e) => console.log(e))
  }

  const familyViewerPhotos = familyPhotos.map(({ image, user }) => ({
    image,
    label: `Photo from ${user.fullname}`,
  }))
  const openFamilyPhoto = ({ id }) =>
    setShownFamilyPhoto(familyPhotos.findIndex((photo) => photo.id === id))

  // Ticked off ingredients: they're probably making it (a follow-up email can ask how
  // it went). The server counts it once a day per recipe.
  const startedCooking = () => {
    trackAction('started_cooking', { recipe_id: dataRecipe.id })
    recordRecipeAction(dataRecipe.id, 'started_cooking', {
      groupId: idGroup,
      ingredientCount: dataRecipe.ingredients?.length,
    })
  }

  // Printed from the browser (its menu or Ctrl/Cmd+P): once per visit
  useEffect(() => {
    if (!dataRecipe?.id) return undefined
    let done = false
    const printed = () => {
      if (done) return
      done = true
      trackAction('printed_recipe', { recipe_id: dataRecipe.id })
      recordRecipeAction(dataRecipe.id, 'printed', { groupId: idGroup })
    }
    window.addEventListener('beforeprint', printed)
    return () => window.removeEventListener('beforeprint', printed)
  }, [dataRecipe?.id, idGroup])

  useEffect(() => {
    dispatch(setLoading(true))
    setLocked(null)
    setFamilyPhotos([])
    getRecipeById(recipeId, false)
      .then(({ data }) => {
        setDataRecipe(data)
      })
      .catch((e) => {
        // Free plan: one of their older recipes, locked until they upgrade
        if (e?.response?.status === 402 && e.response.data?.recipe)
          return setLocked(e.response.data)
        navigation(routes.notFound)
      })
      .finally(() => dispatch(setLoading(false)))
  }, [recipeId])

  if (locked) {
    return (
      <Layout title="Recipe">
        <div className="containerMain">
          <div className="lockedRecipePanel">
            <img
              src={locked.recipe.image || '/images/imageRecipeWithoutPhoto.png'}
              alt=""
              className="lockedRecipePhoto"
            />
            <div>
              <p className="lockedRecipeEyebrow">Locked on the free plan</p>
              <h1 className="lockedRecipeTitle">{locked.recipe.name}</h1>
              <p className="lockedRecipeText">
                The free plan keeps your 10 most recent recipes open. This one is safe, and your
                family can still see it in your groups. Upgrade to Pro to open all your recipes
                again. Cancel any time.
              </p>
              <div className="lockedRecipeActions">
                <Link to={`${routes.settings}#subscription`} className="lockedRecipeUpgrade">
                  Upgrade to Pro
                </Link>
                <Link to={routes.userProfile} className="lockedRecipeBack">
                  Back to my recipes
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout title="Recipe">
      <div className="containerMain">
        <div className={`dashboardWrap ${isNil(idGroup) ? 'justify-content-center' : ''}`}>
          {!isNil(idGroup) ? <TableUsers sliderDisable /> : null}

          <div className="recipeWrap">
            <RecipeHero
              recipe={dataRecipe}
              photos={photos}
              canEdit={canEdit}
              canDelete={checkCurrentUser}
              onDelete={deleteMyRecipe}
              onOpen={setShownPhoto}
            />
            <div className="recipeWrapContent">
              {dataRecipe?.audio && (
                <RecipeAudioPlayer
                  src={dataRecipe.audio}
                  recordedAt={dataRecipe.audio_recorded_at}
                  author={creditPerson(dataRecipe)}
                  seed={dataRecipe.id}
                />
              )}
              {dataRecipe?.id && dataRecipe?.status === 1 ? (
                <AddToCookbook
                  recipeId={dataRecipe.id}
                  groupId={idGroup ? Number(idGroup) : null}
                />
              ) : null}
              {dataRecipe?.audio && checkCurrentUser && user?.features?.cookbook_cards ? (
                <label className="recipeVoiceLink">
                  <input
                    type="checkbox"
                    checked={!dataRecipe.listen_off}
                    onChange={(e) => {
                      const off = !e.target.checked
                      setDataRecipe({ ...dataRecipe, listen_off: off })
                      setVoiceLink(dataRecipe.id, off).catch(() =>
                        setDataRecipe((d) => ({ ...d, listen_off: !off }))
                      )
                    }}
                  />
                  <span>
                    Let printed cookbooks play this voice memo (a QR code anyone with the book can
                    scan)
                  </span>
                </label>
              ) : null}
              <div className="recipeSummaryWrap">
                <div className="innerDescription">
                  {dataRecipe.description && (
                    <>
                      <p className="recipeTitle">Summary</p>
                      <p className="recipeText textDescription">{dataRecipe.description}</p>
                    </>
                  )}
                  {(dataRecipe?.source_name || safeWebLink(dataRecipe?.source_url)) && (
                    <div className="recipeSource">
                      <p className="recipeSourceText">
                        Original recipe from{' '}
                        <span className="recipeSourceName">
                          {dataRecipe.source_name || websiteName(dataRecipe.source_url)}
                        </span>
                      </p>
                      {safeWebLink(dataRecipe?.source_url) && (
                        <a
                          className="recipeSourceButton"
                          href={safeWebLink(dataRecipe.source_url)}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                        >
                          Visit the original recipe
                        </a>
                      )}
                    </div>
                  )}
                </div>

                {!dataRecipe?.audio && (
                  <div className="summaryAvatarWrap">
                    <UserPhoto classStylePhoto="summaryAvatar" image={dataRecipe?.author?.avatar} />
                    <span className="summaryAvatarText">Recipe by:</span>
                    <p className="summaryAvatarName">{creditName(dataRecipe)}</p>
                    {addedByLine(dataRecipe) ? (
                      <span className="summaryAvatarText">{addedByLine(dataRecipe)}</span>
                    ) : null}
                  </div>
                )}
              </div>

              <div className="recipeWrapStep">
                <div className="recipeIngredients ">
                  <p className="recipeTitle">Ingredients</p>
                  <IngredientChecklist
                    recipeId={dataRecipe?.id}
                    ingredients={dataRecipe?.ingredients}
                    onStartedCooking={startedCooking}
                  />

                  <p className="recipeTitle">Serving Size</p>
                  <p className="recipeText">{servingSize}</p>
                </div>
                <div className="recipeLine" />
                <div className="recipeSteps">
                  <p className="recipeTitle">Steps</p>
                  <div>
                    {dataRecipe?.steps?.map(({ step, description }) => (
                      <div key={step} className="recipeStepContent">
                        <p className="recipeNumber">{step}.</p>
                        <p>{description}</p>
                        <br />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {!isEmpty(dataRecipe?.cardImages) && (
                <div className="recipeCardImages" id="original-card">
                  <p className="recipeTitle">
                    The original recipe card
                    {dataRecipe.cardImages.length > 1 ? 's' : ''}
                  </p>
                  <p className="recipeCardImagesNote">
                    Saved just as it was written. Tap a card to see it up close.
                  </p>
                  <div className="recipeCardImagesList">
                    {dataRecipe.cardImages.map(({ id, image }, index) => (
                      <button
                        type="button"
                        key={id}
                        className="recipeCardImageButton"
                        onClick={() => setShownPhoto(photos.findIndex((p) => p.image === image))}
                      >
                        <img
                          src={image}
                          alt={`Original recipe card ${index + 1}`}
                          className="recipeCardImage"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <ButtonReport recipeId={recipeId} />
              {dataRecipe?.id && (!isNil(idGroup) || checkCurrentUser) ? (
                <RecipeComments
                  key={`${recipeId}-${idGroup}`}
                  recipeId={recipeId}
                  groupId={idGroup}
                  recipe={dataRecipe}
                  onPhotos={setFamilyPhotos}
                  onOpenPhoto={openFamilyPhoto}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <RecipePhotoViewer
        photos={photos}
        index={shownPhoto}
        onChange={setShownPhoto}
        onHide={() => setShownPhoto(null)}
      />
      <RecipePhotoViewer
        photos={familyViewerPhotos}
        index={shownFamilyPhoto}
        onChange={setShownFamilyPhoto}
        onHide={() => setShownFamilyPhoto(null)}
      />
    </Layout>
  )
}

export default connect(({ user }) => ({ user: user.data }))(Dashboard)
